import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux'
import dateFormat from 'dateformat'

import SaveButton from '../components/psc/lib/SaveButton'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import CancelButton from '../components/psc/lib/CancelButton'
import DeleteButton from '../components/psc/lib/DeleteButton'
import { PscPhrase } from '../components/psc/psc-phrase';
import FilePaths, { FileButtons } from '../components/psc/lib/FilePaths'
import UserSelect from '../components/psc/lib/UserSelect'
import SingleSelect from '../components/psc/lib/SingleSelect'
import HasTemplate from '../components/psc/lib/HasTemplate'
import TextArea from '../components/psc/lib/TextArea'
import PickDate from '../components/psc/lib/PickDate'
import Title from '../components/psc/lib/Title'
import DocumentVersion from '../components/psc/lib/DocumentVersion'
import Applicability from '../components/psc/lib/Applicability'
import Classification from '../components/psc/lib/Classification'
import HeaderStatusInfo from '../components/psc/lib/HeaderStatusInfo'
import Status from '../components/psc/lib/Status'
import RelatedItems from '../components/psc/lib/RelatedItems'
import CustomFields from '../components/psc/lib/CustomFields'
import Loading from '../components/psc/lib/Loading'
import NoAccess from '../components/psc/lib/NoAccess'

import useAuthentication from '../hooks/useAuthentication';
import useCadmObject from '../hooks/useCadmObject'
import useDocumentStatuses from '../hooks/useDocumentStatuses'
import useUserManagement from '../hooks/useUserManagement'
import useProjectContents from '../hooks/useProjectContents'
import useGncHistory from '../hooks/useGncHistory'

import useSharepointToken from '../hooks/useSharepointToken'

const DocumentSharepointUpload = () => {
    const pscState = useSelector(state => state.pscState.value)
    const config = pscState.config || {}
    const project = pscState.project
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams();
    const isNew = params.objID === 'new';
    const objID = Number(params.objID);

    const { currentUser } = useAuthentication();
    const { encryptionKey, tokenIsValid, redirectToAuthUrl } = useSharepointToken()
    const {
        goBack,
        replaceCurrentPath,
    } = useGncHistory()

    const [id, setID] = useState(objID)
    const [reference, setReference] = useState('')
    const [title, setTitle] = useState('')
    const [statusName, setStatusName] = useState('')
    
    const [hasLoaded, setHasLoaded] = useState(false)
    const [hasSaved, setHasSaved] = useState(false)

    const [ isUploading, setIsUploading ] = useState(false)
    const [ uploadResult, setUploadResult ] = useState(null)
    const [ uploadError, setUploadError ] = useState(null)
    
    useEffect(() => {
        if (isNew) {
            return
        }
        fetchObject()
    }, [])
    
    const fetchObject = () => {
        fetch('/v0/documents/' + objID, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }

                const obj = json.result
                setReference(obj.reference)
                setTitle(obj.title)
                setStatusName(obj.document_status?.name)
                setHasLoaded(true)
            })
            .catch(err => {
                console.log("Document fetch error", err)
            })
        
    }

    const handleAuthenticate = () => {
        redirectToAuthUrl()
    }

    const handleUpload = async () => {
        setIsUploading(true)
        setUploadResult(null)
        setUploadError(null)
        
        try {
            const response = await fetch(
                `/v0/documents/${objID}/upload_to_sharepoint?encryption_key=${encryptionKey}`,
                {
                    credentials: 'same-origin',
                    method: 'POST'
                }
            )
                
            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Upload failed')
            }
            
            const json = await response.json()
            setUploadResult(json.result)
        } catch (error) {
            setUploadError(error.message)
        } finally {
            setIsUploading(false)
            setHasSaved(true)
        }
    }

    const handleOpenDocument = () => {
        const obj = uploadResult
        const path = obj.file_paths?.[0]
        window.open(path, "_blank", "noopener,noreferrer")
    }
    
    const pageContent = useMemo(() => {
        if (!hasLoaded) {
            return <Loading inline={true}/>
        }

        if (!tokenIsValid) {
            return (
                <>
                <p>
                    To save the document to Sharepoint, you must first authenticate.
                </p>
                <button 
                    className="btn btn-primary" 
                    onClick={handleAuthenticate}
                >
                    Authenticate via Sharepoint
                </button>
                </>
            )
        }
        
        if (hasSaved) {
            return (
                <>
                <p>
                    The document has been saved in Sharepoint!
                </p>
                <button 
                    className="btn btn-primary" 
                    onClick={handleOpenDocument}
                >
                    Open the Document
                </button>
                </>
            )
            
        }
        
        return (
            <>
            <p>
                To save the document to Sharepoint, click the button below
            </p>
            <button 
                className="btn btn-primary" 
                onClick={handleUpload}
                disabled={isUploading}
            >
                {isUploading ?
                 <><i className="fa fa-refresh fa-spin fa-fw"></i> Uploading...</>
                 : 
                     <><i className="fa fa-cloud-upload"></i> Upload to SharePoint</>
                }
            </button>
            
            {uploadResult &&
             <span className="text-success" style={{marginLeft: '10px'}}>
                 <i className="fa fa-check"></i> Uploaded successfully
             </span>
            }
            
            {uploadError && (
                 <span className="text-danger" style={{marginLeft: '10px'}}>
                     <i className="fa fa-exclamation-triangle"></i> {uploadError}
                 </span>
             )}
            </>
        )
    }, [hasLoaded, tokenIsValid, hasSaved])
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            
                            <HeaderStatusInfo
                                type="Document"
                                id={id}
                                status={statusName}
                            />

                            <Title
                                type="Document"
                                reference={reference}
                                title={title}
                                id={id}
                                showLatest={true}
                            />
                        </div>
                        <div className="ibox-content">
                            {pageContent}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default DocumentSharepointUpload
